<script setup lang="ts">
import type { createCalendarControlsPlugin } from '@schedule-x/calendar-controls';
import type { ScheduleXViewId } from './LayoutCalendar.vue';
import { Temporal } from 'temporal-polyfill';
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUserLocale } from '#layers/base/public';

const {
	range,
	calendarControls,
} = defineProps<{
	range: { start: Temporal.PlainDate; end: Temporal.PlainDate };
	calendarControls: ReturnType<typeof createCalendarControlsPlugin>;
}>();

const { t } = useI18n();
const { formatLocale } = useUserLocale();

const currentView = defineModel<ScheduleXViewId>({ required: true });

const viewOptions: { label: string; value: ScheduleXViewId }[] = [
	{ label: t('layout.calendar.views.month'), value: 'month-grid' },
	{ label: t('layout.calendar.views.week'), value: 'week' },
	{ label: t('layout.calendar.views.day'), value: 'day' },
	{ label: t('layout.calendar.views.list'), value: 'list' },
];

function getSmallNavigationUnit() {
	switch (currentView.value) {
		case 'month-grid':
		case 'list':
			return t('layout.calendar.views.month').toLowerCase();
		case 'week':
			return t('layout.calendar.views.week').toLowerCase();
		case 'day':
			return t('layout.calendar.views.day').toLowerCase();
	}
}

function getLargeNavigationUnit() {
	switch (currentView.value) {
		case 'month-grid':
		case 'list':
			return t('layout.calendar.views.year').toLowerCase();
		case 'week':
		case 'day':
			return t('layout.calendar.views.month').toLowerCase();
	}
}

const prevLargeLabel = computed(() => t('layout.calendar.nav.previous', { unit: getLargeNavigationUnit() }));
const prevLabel = computed(() => t('layout.calendar.nav.previous', { unit: getSmallNavigationUnit() }));
const nextLabel = computed(() => t('layout.calendar.nav.next', { unit: getSmallNavigationUnit() }));
const nextLargeLabel = computed(() => t('layout.calendar.nav.next', { unit: getLargeNavigationUnit() }));

const headerTitle = computed(() => {
	const { start, end } = range;

	if (currentView.value === 'day') {
		return start.toLocaleString(formatLocale.value, { month: 'long', day: 'numeric', year: 'numeric' });
	}

	if (currentView.value === 'week') {
		return `${start.toLocaleString(formatLocale.value, { month: 'short', day: 'numeric' })} – ${end.toLocaleString(formatLocale.value, { month: 'short', day: 'numeric', year: 'numeric' })}`;
	}

	const midDay = Math.floor(start.until(end).total({ unit: 'days' }) / 2);
	const mid = start.add({ days: midDay });
	return mid.toLocaleString(formatLocale.value, { month: 'long', year: 'numeric' });
});

// Some custom functions for the calendar layout
// We use these instead of the components from schedule-x to give it a more Monospace feeling
function getNavigationDelta(): Temporal.DurationLike {
	switch (currentView.value) {
		case 'month-grid':
		case 'list':
			return { months: 1 };
		case 'week':
			return { weeks: 1 };
		case 'day':
			return { days: 1 };
	}
}

function getLargeNavigationDelta(): Temporal.DurationLike {
	switch (currentView.value) {
		case 'month-grid':
		case 'list':
			return { years: 1 };
		case 'week':
		case 'day':
			return { months: 1 };
	}
}

function goToday() {
	calendarControls.setDate(Temporal.Now.plainDateISO());
}

function goNext() {
	const date = calendarControls.getDate();
	calendarControls.setDate(date.add(getNavigationDelta()));
}

function goPrev() {
	const date = calendarControls.getDate();
	calendarControls.setDate(date.subtract(getNavigationDelta()));
}

function goNextLarge() {
	const date = calendarControls.getDate();
	calendarControls.setDate(date.add(getLargeNavigationDelta()));
}

function goPrevLarge() {
	const date = calendarControls.getDate();
	calendarControls.setDate(date.subtract(getLargeNavigationDelta()));
}

watch(currentView, (view) => {
	calendarControls.setView(view);
});
</script>

<template>
	<div class="@container relative flex shrink-0 items-center px-6 py-1.5">
		<div class="flex items-center justify-center gap-1">
			<DButton
				icon="lucide:chevrons-left"
				color="neutral"
				variant="ghost"
				square
				size="xs"
				:aria-label="prevLargeLabel"
				:title="prevLargeLabel"
				class="hidden @w-sm:flex"
				@click="goPrevLarge"
			/>
			<DButton icon="lucide:chevron-left" color="neutral" variant="ghost" square size="xs" :aria-label="prevLabel" :title="prevLabel" @click="goPrev" />
			<DButton icon="lucide:chevron-right" color="neutral" variant="ghost" square size="xs" :aria-label="nextLabel" :title="nextLabel" @click="goNext" />
			<DButton
				icon="lucide:chevrons-right"
				color="neutral"
				variant="ghost"
				square
				size="xs"
				:aria-label="nextLargeLabel"
				:title="nextLargeLabel"
				class="hidden @w-sm:flex"
				@click="goNextLarge"
			/>
		</div>

		<DButton color="neutral" variant="outline" size="sm" class="ms-2 hidden @w-sm:block" @click="goToday">
			{{ $t('layout.calendar.today') }}
		</DButton>

		<span class="pointer-events-none inset-0 flex items-center justify-center text-sm font-semibold sm:absolute">{{ headerTitle }}</span>
		<span class="flex-1" />

		<DSelect
			v-model="currentView"
			:items="viewOptions"
			size="sm"
			class="w-28"
		/>
	</div>
</template>
